import React, {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete as MuiAutocomplete,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  InputLabelProps,
  MenuItem,
  Switch,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
} from '@mui/material';
import styles from './TextField.module.scss';
import cx from 'classnames';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import Alert from '../Alert/Alert';
import useDelayedSearch, {
  UseDelayedSearch,
} from '../../hooks/useDelayedSearch/useDelayedSearch';
import { Asset } from '../../domain/Asset';
import Button from '../Button/Button';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import Loader from '../Loader/Loader';
import { translate } from '../../utility/messageTranslator/translate';
import { useIntl } from 'react-intl';
import { Editor } from '@tinymce/tinymce-react';
import FileInput from '../FileInput/FileInput';
import { HttpError } from '../../config/Axios/axios-instance';
import * as assetService from '../../store/asset/service';
import { StoreState } from '../../config/StoreProvider/StoreProvider';
import { connect } from 'react-redux';
import { Locale } from '../../domain/Translation';
import { enGB, lt } from 'date-fns/esm/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TimePicker } from '@mui/x-date-pickers';
import SignaturePad from 'react-signature-canvas';
import ClearIcon from '@mui/icons-material/Clear';
import useWindowSize from '../../hooks/useWindowSize/useWindowSize';
import { ReactSVG } from 'react-svg';
import eye from '../../assets/icons/eye.svg';
import eyeSlash from '../../assets/icons/eye-slash.svg';
import arrowIcon from '../../assets/icons/dropdown-arrow.svg';

type RadioInput = {
  value?: number | string;
  label?: string;
};

export type RadioProps = {
  value: string;
  radioInputs: RadioInput[];
};

type Props = {
  selectedLocale: Locale;
  onChange?: (event: ChangeEvent<any>) => void;
  onFileChange?: (event: ChangeEvent<any>) => void;
  onCheckboxChange?: (name: string, value: string) => void;
  onInputValueChange?: (name: string, value: string) => void;
  onTimeChange?: (name: string, value: string) => void;
  onBlur?: (event: ChangeEvent<any>) => void;
  onInputBlur?: (name: string) => void;
  onImageDelete?: (name: string, value: string | File) => void;
  onSetValidationErrors?: (error: HttpError) => void;
  value?: string | string[] | File | File[];
  label?: string | ReactNode;
  errors?: Array<string>;
  name: string;
  type?: string;
  placeholder?: string;
  inputProps?: Partial<StandardInputProps>;
  inputLabelProps?: Partial<InputLabelProps>;
  className?: string;
  options?: Array<{ value: string; label: string }>;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  variant?: 'filled' | 'outlined' | 'standard';
  isOutlined?: boolean;
  disabled?: boolean;
  helperText?: string;
  clearable?: boolean;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  onSelectChange?: (value: string | string[], name: string) => void;
  isLoading?: boolean;
  onSearch?: (searchValue: string) => void;
  moreLabel?: string;
  asset?: Asset | null;
  creatable?: boolean;
  onCreate?: () => void;
  autoFocus?: boolean;
  isSmall?: boolean;
  maxFileCount?: number;
  minDate?: string;
  optionAsIcon?: boolean;
  maxDate?: moment.Moment | string;
  onlyPdf?: boolean;
  required?: boolean;
  labelClassname?: string;
  isDefaultCheckboxClassIgnored?: boolean;
  publicTheme?: boolean;
  radioProps?: RadioProps;
  onRadioChange?: (name: string, value: string) => void;
};

const TextField = ({
  selectedLocale,
  onChange,
  onBlur,
  value,
  label,
  errors,
  name,
  type,
  placeholder,
  inputProps,
  inputLabelProps,
  className,
  onFileChange,
  onCheckboxChange,
  options,
  labelPlacement,
  onTimeChange,
  disabled,
  helperText,
  clearable,
  multiple,
  disableCloseOnSelect,
  disableClearable,
  onSelectChange,
  isLoading = false,
  onSearch,
  moreLabel,
  asset,
  creatable,
  onCreate,
  autoFocus,
  variant = 'outlined',
  isSmall,
  onInputBlur,
  onInputValueChange,
  maxFileCount,
  onImageDelete,
  onSetValidationErrors,
  minDate,
  optionAsIcon,
  maxDate,
  onlyPdf,
  required,
  labelClassname,
  isDefaultCheckboxClassIgnored,
  publicTheme = false,
  radioProps,
  onRadioChange,
}: Props) => {
  const [autocompleteInput, setAutocompleteInput] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const intl = useIntl();
  const { width } = useWindowSize();

  const signatureRef = useRef<any>();

  const showErrors = useMemo(() => variant !== 'filled', [variant]);
  const adapterLocale = useMemo(() => {
    if (selectedLocale === 'lt') {
      return lt;
    }

    return enGB;
  }, [selectedLocale]);

  const { setSearchValue }: UseDelayedSearch = useDelayedSearch(
    !!onSearch ? onSearch : () => {},
  );

  useEffect(() => {
    if (type === 'autocomplete') {
      setSearchValue(autocompleteInput);
    }
  }, [autocompleteInput]);

  useEffect(() => {
    if (type === 'richtext') {
      document.addEventListener('focusin', handleRichTextBoxFocusIn);
    }
  }, []);

  useEffect(() => {
    return () =>
      document.removeEventListener('focusin', handleRichTextBoxFocusIn);
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRichTextBoxFocusIn = (event: FocusEvent) => {
    if (
      event.target &&
      // @ts-ignore
      event.target.closest(
        '.tox-tinymce-aux, .moxman-window, .tam-assetmanager-root',
      ) !== null
    ) {
      event.stopImmediatePropagation();
    }
  };

  const getTextFieldClass = () => {
    return publicTheme ? styles.textFieldPublic : styles.textField;
  };

  if (type === 'autocomplete') {
    const getSelectionValue = () => {
      if (Array.isArray(value)) {
        return options?.filter((option) =>
          value.toString().includes(option.value),
        );
      }

      if (multiple) {
        const values = value?.toString().split(',');
        return options?.filter(
          (option) =>
            !!values?.find(
              (singleValue: any) =>
                singleValue.toString() === option.value.toString(),
            ),
        );
      }

      return options?.find(
        (option) => option.value.toString() === value?.toString(),
      );
    };

    const getOptions = () => {
      if (!options) {
        return [];
      }

      if (moreLabel) {
        return [...options, { label: moreLabel, value: '' }];
      }

      return options;
    };

    return (
      <MuiAutocomplete
        id={name}
        className={styles.autocomplete}
        multiple={multiple}
        options={getOptions()}
        getOptionLabel={(option: any) => option.label}
        value={getSelectionValue() ?? null}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        disableClearable={disableClearable}
        inputValue={multiple ? autocompleteInput : undefined}
        getOptionDisabled={(option) => option.value === ''}
        onChange={(_: any, values: any) => {
          setAutocompleteInput('');
          if (Array.isArray(values)) {
            return onSelectChange?.(
              (values as any[]).map(({ value }) => value),
              name,
            );
          }

          onSelectChange?.(values?.value || '', name);
        }}
        onBlur={onBlur}
        loading={isLoading}
        renderOption={(option) => {
          // @ts-ignore
          const optionKey = option.key;
          const icon = optionAsIcon && (
            <FontAwesomeIcon
              className={styles.iconSelect}
              icon={optionKey}
              fixedWidth
              size="1x"
            />
          );

          return (
            <li {...option}>
              {icon} {optionKey}
            </li>
          );
        }}
        renderInput={(params: any) => (
          <MuiTextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={
              showErrors && errors && errors.length > 0 ? errors[0] : ''
            }
            error={errors && errors.length > 0}
            name={name}
            className={cx(getTextFieldClass(), className)}
            variant={variant as any}
            size={isSmall ? 'small' : 'medium'}
            onChange={(event) => setAutocompleteInput(event.target.value)}
            InputProps={{
              ...params.InputProps,
              disableUnderline: variant === 'filled' && !errors?.length,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {creatable && (
                    <Button buttonVariant="text" onClick={() => onCreate?.()}>
                      {translate(intl, 'INPUTS.CREATE')}
                    </Button>
                  )}
                  {publicTheme && (
                    <InputAdornment position="end">
                      <ReactSVG
                        src={arrowIcon}
                        className={styles.dropdownIcon}
                      />
                    </InputAdornment>
                  )}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  }

  if (type === 'file') {
    return (
      <FileInput
        value={value as File | File[] | string | string[] | undefined}
        name={name}
        helperText={helperText}
        label={label}
        onFileChange={onFileChange}
        maxFileCount={maxFileCount}
        multiple={multiple}
        errors={errors}
        asset={asset}
        onImageDelete={onImageDelete}
        onSetValidationErrors={onSetValidationErrors}
        onlyPdf={onlyPdf}
      />
    );
  }

  if (type === 'currency') {
    return (
      <>
        {helperText && <Alert variant="warning">{helperText}</Alert>}
        <MuiTextField
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          label={label}
          error={errors && errors.length > 0}
          variant={variant as any}
          name={name}
          helperText={
            showErrors && errors && errors.length > 0 ? errors[0] : ''
          }
          className={cx(getTextFieldClass(), styles.currencyInput, className)}
          type="number"
          disabled={disabled}
          placeholder={placeholder}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
            startAdornment: <InputAdornment position="start">€</InputAdornment>,
          }}
          size={isSmall ? 'small' : 'medium'}
        />
      </>
    );
  }

  if (type === 'time') {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={adapterLocale}
      >
        <TimePicker
          ampm={false}
          value={
            typeof value === 'string' ? moment(value, 'HH:mm').toDate() : ''
          }
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(name, moment(newValue?.toString()).format('HH:mm'));
          }}
          label={label}
          disabled={disabled}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(getTextFieldClass(), styles.timeInput, className)}
              onBlur={onBlur}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'staticdate') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          openTo="day"
          label={label}
          value={value}
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(
                name,
                newValue
                  ? moment(newValue.toString()).format('YYYY-MM-DD')
                  : '',
              );
          }}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(getTextFieldClass(), className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'datetime') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          views={['year', 'month', 'day', 'hours', 'minutes']}
          label={label}
          value={value}
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(
                name,
                newValue
                  ? moment(newValue.toString()).format('YYYY-MM-DD HH:mm')
                  : '',
              );
          }}
          mask="____-__-__ __:__"
          disabled={disabled}
          inputFormat="yyyy-MM-dd HH:mm"
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          maxDate={maxDate}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(getTextFieldClass(), className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'date') {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={adapterLocale}
      >
        <DateTimePicker
          views={['year', 'month', 'day']}
          label={label}
          value={value}
          onChange={(newValue: any, keyboardInputValue) => {
            if (!onTimeChange) {
              return;
            }

            if (
              !keyboardInputValue ||
              /^\d{4}-\d{2}-\d{2}$/.test(keyboardInputValue)
            ) {
              onTimeChange(
                name,
                newValue ? moment(newValue).format('YYYY-MM-DD') : '',
              );
            } else {
              onTimeChange(name, 'Invalid date');
            }
          }}
          mask="____-__-__"
          disabled={disabled}
          inputFormat="yyyy-MM-dd"
          minDate={minDate ? new Date(minDate) : undefined}
          maxDate={maxDate ? moment(maxDate).toDate() : undefined}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(getTextFieldClass(), className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              placeholder={placeholder}
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'switcher') {
    return (
      <FormControlLabel
        className={cx(styles.switcherContainer, className)}
        labelPlacement={labelPlacement}
        control={
          <Switch
            name={name}
            checked={value === '1'}
            onChange={() =>
              onCheckboxChange &&
              onCheckboxChange(name, value === '0' ? '1' : '0')
            }
          />
        }
        disabled={disabled}
        label={label}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <>
        <FormControlLabel
          className={cx(
            { [styles.checkboxContainer]: !isDefaultCheckboxClassIgnored },
            className,
          )}
          labelPlacement={labelPlacement}
          control={
            <Checkbox
              checked={value === '1'}
              onChange={() =>
                onCheckboxChange &&
                onCheckboxChange(name, value === '0' ? '1' : '0')
              }
            />
          }
          disabled={disabled}
          label={label}
        />

        {errors && errors.length > 0 && (
          <div className={styles.checkboxError}>{errors[0]}</div>
        )}
      </>
    );
  }

  if (type === 'select') {
    return (
      <MuiTextField
        value={value}
        select
        onChange={onChange}
        onBlur={onBlur}
        label={label}
        error={errors && errors.length > 0}
        variant={variant as any}
        name={name}
        helperText={showErrors && errors && errors.length > 0 ? errors[0] : ''}
        className={cx(getTextFieldClass(), className)}
        type="number"
        placeholder={placeholder}
        size={isSmall ? 'small' : 'medium'}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        )) ?? []}
      </MuiTextField>
    );
  }

  if (type === 'richtext') {
    return (
      <div
        className={cx(styles.editorContainer, {
          [styles.error]: errors && errors.length,
        })}
      >
        <Editor
          apiKey={process.env.REACT_APP_TINYMCE_KEY}
          disabled={disabled}
          value={value?.toString()}
          onEditorChange={(value) =>
            onInputValueChange && onInputValueChange(name, value)
          }
          onBlur={(event) => {
            event.preventDefault();

            onInputBlur && onInputBlur(name);
          }}
          init={{
            automatic_uploads: true,
            file_picker_types: 'image',
            plugins: 'image',
            toolbar:
              'undo redo | formatselect | ' +
              'bold italic backcolor image | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | ' +
              'removeformat | help',
            images_upload_handler: async (blobInfo) => {
              return new Promise(async (resolve, reject) => {
                await assetService.createAsset(
                  blobInfo.blob(),
                  resolve,
                  reject,
                );
              });
            },
          }}
        />
        {errors &&
          errors.map((error, index) => (
            <div className={styles.editorError} key={`editor-error-${index}`}>
              {error}
            </div>
          ))}
      </div>
    );
  }

  if (type === 'signature') {
    const onEndDrawing = () => {
      const signatureData = signatureRef.current.toDataURL();

      onInputValueChange &&
        onInputValueChange(
          name,
          signatureData.replace('data:image/png;base64,', ''),
        );
    };

    const handleClear = () => {
      signatureRef.current?.clear();

      onInputValueChange && onInputValueChange(name, '');
    };

    return (
      <div className={styles.signatureInputContainer}>
        <div
          className={cx(styles.signatureContainer, {
            [styles.error]: errors && errors.length,
          })}
        >
          {label && <span className={styles.label}>{label}</span>}
          <SignaturePad
            ref={signatureRef}
            canvasProps={{
              width: width && width < 400 ? 232 : 300,
              height: 150,
              className: styles.canvas,
            }}
            onEnd={onEndDrawing}
          />
          {clearable && value && (
            <ClearIcon
              className={styles.clearButton}
              onClick={() => handleClear()}
            />
          )}
        </div>

        {errors &&
          errors.map((error, index) => (
            <div
              className={styles.signatureError}
              key={`signature-error-${index}`}
            >
              {error}
            </div>
          ))}
      </div>
    );
  }

  if (type === 'password') {
    return (
      <Loader isLoading={isLoading} size="2xs">
        {publicTheme && <div className={styles.label}>{label}</div>}
        <MuiTextField
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          error={errors && errors.length > 0}
          variant={variant as any}
          label={publicTheme ? '' : label}
          name={name}
          helperText={
            showErrors && errors && errors.length > 0 ? errors[0] : ''
          }
          className={cx(getTextFieldClass(), className)}
          type={showPassword ? 'text' : 'password'}
          placeholder={`${placeholder}${required ? ' *' : ''}`}
          InputProps={{
            ...inputProps,
            endAdornment: (
              <InputAdornment position="end">
                <ReactSVG
                  src={showPassword ? eyeSlash : eye}
                  onClick={togglePasswordVisibility}
                  className={styles.eyeIcon}
                />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            ...inputLabelProps,
          }}
          disabled={disabled}
          autoFocus={autoFocus}
          size={isSmall ? 'small' : 'medium'}
        />
      </Loader>
    );
  }

  if (type === 'radio' && radioProps && onRadioChange) {
    return (
      <RadioGroup
        value={value}
        onChange={(event) => onRadioChange(name, event.target.value)}
        name={name}
        row
      >
        {radioProps.radioInputs.map((radioInput) => (
          <FormControlLabel
            key={radioInput.value}
            value={radioInput.value}
            className={styles.radioInputContainer}
            control={<Radio />}
            label={radioInput.label}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <Loader isLoading={isLoading} size="2xs">
      {publicTheme && <div className={styles.label}>{label}</div>}
      <MuiTextField
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={errors && errors.length > 0}
        variant={variant as any}
        label={publicTheme ? '' : label}
        name={name}
        helperText={showErrors && errors && errors.length > 0 ? errors[0] : ''}
        className={cx(getTextFieldClass(), className)}
        type={type}
        placeholder={placeholder ? `${placeholder}${required ? ' *' : ''}` : ''}
        InputProps={{
          ...inputProps,
        }}
        InputLabelProps={{ ...inputLabelProps }}
        multiline={type === 'textarea'}
        rows={5}
        disabled={disabled}
        autoFocus={autoFocus}
        size={isSmall ? 'small' : 'medium'}
      />
    </Loader>
  );
};

const mapStateToProps = (state: StoreState) => ({
  selectedLocale: state.auth.selectedLocale,
});

export default connect(mapStateToProps)(TextField);
